import { Link } from "@tanstack/react-router";
import { Wordmark } from "@/components/brand-mark";

type FooterLink = { to: string; label: string; params?: Record<string, string> };

const USE: FooterLink[] = [
  { to: "/map", label: "Map" },
  { to: "/tick-identification", label: "Identify a tick" },
  { to: "/tick-bite-tool", label: "I was bitten" },
  { to: "/prevention", label: "Prevent bites" },
  { to: "/tick-bite-guide", label: "Printable bite checklist" },
  { to: "/states", label: "Every state" },
];

const LEARN: FooterLink[] = [
  { to: "/alpha-gal", label: "Alpha-gal syndrome" },
  { to: "/$guide", params: { guide: "alpha-gal-symptoms" }, label: "Alpha-gal symptoms" },
  { to: "/ticks", label: "Tick species" },
  { to: "/diseases", label: "Tick-borne diseases" },
  { to: "/$guide", params: { guide: "tick-season-by-state" }, label: "Tick season by state" },
  {
    to: "/$guide",
    params: { guide: "where-do-lone-star-ticks-live" },
    label: "Where Lone Star ticks live",
  },
  { to: "/resources", label: "Articles" },
];

const ABOUT: FooterLink[] = [
  { to: "/about", label: "About this guide" },
  { to: "/sources", label: "Sources" },
  { to: "/contact", label: "Contact" },
  { to: "/disclaimer", label: "Disclaimer" },
  { to: "/privacy", label: "Privacy" },
  { to: "/terms", label: "Terms" },
  { to: "/cookies", label: "Cookies" },
];

/**
 * A colophon, not a sitemap dump: what this is, who it's from, the one safety line that
 * matters, then three short columns.
 */
export function SiteFooter() {
  return (
    <footer className="no-print mt-24 border-t border-rule/15 bg-paper-deep">
      <div className="mx-auto grid max-w-6xl gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[1.3fr_2fr]">
        <div className="max-w-sm">
          <Link to="/" aria-label="LoneStarMap home" className="inline-block rounded-sm">
            <Wordmark />
          </Link>
          <p className="mt-5 font-display text-xl leading-snug text-foreground">
            A free field guide to the Lone Star tick and alpha-gal syndrome.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Built from CDC surveillance and published research, and reviewed against its sources. It
            is not medical advice. If someone is struggling to breathe, call 911.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3">
          <FooterCol title="Use it" links={USE} />
          <FooterCol title="Learn" links={LEARN} />
          <FooterCol title="About" links={ABOUT} />
        </div>
      </div>
      <div className="border-t border-border">
        <div className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-5 text-xs text-muted-foreground sm:flex-row sm:justify-between sm:px-6">
          <p>© {new Date().getFullYear()} LoneStarMap</p>
          <p>No ads, no accounts, nothing sold.</p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: FooterLink[] }) {
  return (
    <div>
      <h2 className="label-caps text-muted-foreground">{title}</h2>
      <ul className="mt-4 space-y-2.5 text-sm">
        {links.map((l) => (
          <li key={l.label}>
            <Link
              to={l.to}
              params={l.params}
              className="text-foreground/85 underline-offset-4 hover:text-primary hover:underline"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
